<template>
  <div>
    <div class="table-responsive">
      <table class="table table-vcenter" style="table-layout: fixed">
        <thead>
          <tr>
            <th class="icon-col"></th>
            <th>{{ $t('amount') }}</th>
            <th>{{ $t('description') }}</th>
            <th class="text-end pe-3">{{ $t('settings.company.orders.created_at') }}</th>
          </tr>
        </thead>
        <RemoteListView
          :endpoint="props.endpoint"
          :search-query="''"
          order="created_at"
          order-dir="desc"
          :page-size="100"
          element-type="tbody">
          <template #collection="{ collection }">
            <tr v-for="item in collection as IVbudgetTransaction[]" :key="item.id">
              <td class="icon-col">
                <IconCircleArrowUp stroke-width="1.5" v-if="item.type === 'refund'" class="text-success ms-3" />
                <IconCircleArrowDown stroke-width="1.5" v-if="item.type === 'withdrawal'" class="text-danger ms-3" />
              </td>
              <td class="text-nowrap">
                <p class="m-0">
                  <span>{{ formatCurrency(item.amount, item.currency) }}</span>
                  <span v-if="item.type === 'refund'" class="ms-2 status status-green">{{ $t('refund') }}</span>
                  <span v-else-if="item.type === 'withdrawal'" class="ms-2 status status-azure">{{
                    $t('withdraw')
                  }}</span>
                </p>
              </td>
              <td class="text-nowrap text-truncate">
                <span v-tooltip="item.description">{{ item.description }}</span>
              </td>
              <td class="text-end text-nowrap pe-3">
                <div class="d-flex gap-1 flex-column">
                  <p class="m-0" v-tooltip="`${formatDateTime(item.created_at)}`">
                    {{
                      formatDistanceToNow(new Date(item.created_at || ''), {
                        addSuffix: true,
                        locale: currentLanguage === 'en_US' ? enUS : de,
                      })
                    }}
                  </p>
                </div>
              </td>
            </tr>
          </template>
          <template #footer="{ page, changePage, refresh, startRecordIndex, endRecordIndex, filterCount, pageCount }">
            <tr>
              <td colspan="4">
                <div class="d-flex align-items-center justify-content-between ms-3">
                  <p class="m-0 text-muted">
                    <small>
                      {{ startRecordIndex }} - {{ endRecordIndex }} {{ $t('of') }} <strong>{{ filterCount }}</strong>
                      {{ $t('entries') }}
                    </small>
                  </p>
                  <LiPagination
                    :modelValue="page"
                    @update:modelValue="changePage"
                    :page-count="pageCount"
                    class="m-0 ms-auto"
                    @change="refresh"
                    :labels="{ prev: $t('orders.pagination.prev'), next: $t('orders.pagination.next') }" />
                </div>
              </td>
            </tr>
          </template>
          <template #empty>
            <tr>
              <td colspan="4">
                <EmptyState :title="$t('billing.budget.transactions.empty')" :icon="IconMoodEmpty" />
              </td>
            </tr>
          </template>
          <template #loading>
            <tr>
              <td colspan="4">
                <div class="d-flex w-full justify-content-center align-items-center">
                  <span class="spinner-border" />
                </div>
              </td>
            </tr>
          </template>
        </RemoteListView>
      </table>
    </div>
  </div>
</template>
<script setup lang="ts">
import { currentLanguage } from '@/locale/translator';
import RemoteListView from '../RemoteListView.vue';
import { formatDistanceToNow } from 'date-fns';
import { enUS, de } from 'date-fns/locale';
import { Pagination as LiPagination } from '@prospective/lithium';
import { IconMoodEmpty, IconCircleArrowUp, IconCircleArrowDown } from '@tabler/icons-vue';
import EmptyState from '@/components/common/EmptyState.vue';
import { formatDateTime } from '@/filters/dateFormat';
import { formatCurrency } from '@/locale/format';
import type { IVbudgetTransaction } from '@/types/budget';

const props = defineProps<{
  endpoint: string;
}>();
</script>
<style scoped>
tr > th {
  color: black !important;
  font-weight: 700;
}

.icon-col {
  width: 50px;
}
</style>
